import { ColumnDef, Row } from '@tanstack/react-table';
import { ActionMeta, OnChangeValue } from 'chakra-react-select';
import equal from 'fast-deep-equal';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ComboboxProps } from '../../../combobox';
import { EditablePopover } from '../../../editable';
import { FloatingActionButtons } from '../../../floating-action-buttons';
import { OverflowContainer } from '../../../overflow-tooltip';
import { BulkChangeConfigBase, useSyncIsBulkLoading } from '../../bulk-actions';
import { type CanEditGuard, getEditable, ifBulkEditable, TableColumnType } from '../../table';
import { CellContainer } from '../cell';
import {
  createOptionColumnHelper,
  OptionColumnConfig,
  OptionColumnDef,
  OptionHelperConfig,
} from './option-column';

type SelectHelperConfig<TOptionBase, TExtraColumnDef> = {
  columnType: Extract<TableColumnType, 'avatar' | 'status' | 'tag'>;
  getOptionValue: OptionHelperConfig<TOptionBase, TExtraColumnDef>['getOptionValue'];
  getOptionLabel: OptionHelperConfig<TOptionBase, TExtraColumnDef>['getOptionLabel'];

  components: OptionHelperConfig<TOptionBase, TExtraColumnDef>['components'];
};

type SelectColumnDef<TData, TOption, IsMulti extends boolean, TFilter, TSort> = OptionColumnDef<
  TData,
  TOption,
  IsMulti,
  TFilter,
  TSort
> &
  SelectColumnConfig<TData, TOption, IsMulti>;

export type BulkSelectOnChange<TData, TOption, IsMulti extends boolean> = (
  rows: Row<TData>[],
  value: OnChangeValue<TOption, IsMulti>,
  meta: ActionMeta<TOption>,
) => unknown;
export type UseBulkSelectChangeHook<TData, TOption, IsMulti extends boolean> = () => readonly [
  BulkSelectOnChange<TData, TOption, IsMulti>,
  boolean,
];

type SelectColumnConfig<TData, TOption, IsMulti extends boolean> = {
  edit?: SelectEditConfig<TData, TOption, IsMulti>;
  bulkEdit?: {
    useBulkChange: UseBulkSelectChangeHook<TData, TOption, IsMulti>;
    bulkInitialValue?: OnChangeValue<TOption, IsMulti>;
    stayOpenOnChange?: boolean;
    isClearable?: boolean;
    hideClearIndicator?: boolean;
    onClose?: () => unknown;
  } & Omit<BulkChangeConfigBase, 'onClick' | 'wrapper'>;
};

type SelectEditConfig<TData, TOption, IsMulti extends boolean> = Omit<
  Partial<ComboboxProps<TOption, IsMulti>>,
  'isMulti' | 'value' | 'onChange'
> & {
  canEditGuard?: CanEditGuard<TData, OnChangeValue<TOption, IsMulti> | undefined>;
  closeOnChange?: boolean;
  onChange: (
    row: Row<TData>,
    value: OnChangeValue<TOption, IsMulti>,
    meta: ActionMeta<TOption>,
  ) => unknown;
};

/**
 * Editable select column helper built on {@link createOptionColumnHelper}.
 * Adds cell `edit` / `bulkEdit` for tag, status, and avatar columns.
 */
export function createSelectHelper<TOptionBase extends object, TExtraColumnDef = object>({
  columnType,
  getOptionValue,
  getOptionLabel,
  components,
}: SelectHelperConfig<TOptionBase, TExtraColumnDef>) {
  const createOptionColumn = createOptionColumnHelper<TOptionBase, TExtraColumnDef>({
    columnType,
    getOptionValue,
    getOptionLabel,
    components,
  });

  return <TData, TFilter, TSort>() => {
    const optionColumn = createOptionColumn<TData, TFilter, TSort>();

    return <TOption extends TOptionBase, IsMulti extends boolean = false>(
      columnDef: SelectColumnDef<TData, TOption, IsMulti, TFilter, TSort> & TExtraColumnDef,
    ): ColumnDef<TData, OnChangeValue<TOption, IsMulti> | undefined> => {
      const id = columnDef.id ?? '';
      const { edit, bulkEdit, isMulti } = columnDef;
      const useBulkChange = bulkEdit?.useBulkChange;
      const editable = getEditable(edit);

      const getValue = <
        V extends OnChangeValue<TOption, IsMulti>,
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
        T extends unknown,
      >(
        rows: Row<T>[],
        columnId: string,
        isMulti?: IsMulti,
      ): V | undefined => {
        if (isMulti) {
          const rowsOptions = rows.map((row) => row.getValue<V>(columnId)) as V[][];
          const valuesIntersection = rowsOptions.reduce(
            (rowA, rowB) => rowA?.filter((valueA) => rowB?.some((valueB) => equal(valueA, valueB))),
            rowsOptions[0] ?? [],
          );
          return valuesIntersection as V;
        }
        const [firstRow, ...restRows] = rows;
        if (!firstRow) return undefined;

        const value = firstRow.getValue<V>(columnId);
        return restRows.every((row) => equal(row.getValue(columnId), value)) ? value : undefined;
      };

      const baseColumn = optionColumn(
        columnDef as OptionColumnDef<TData, TOption, IsMulti, TFilter, TSort> & TExtraColumnDef,
      );

      return {
        ...baseColumn,

        cell: (context) => {
          const value = context.getValue();
          const buttons = columnDef.meta?.cell?.actions?.(context.row) ?? [];
          const content = <components.Preview columnDef={columnDef} value={value} />;
          const preview = (
            <CellContainer p={0} cell={context.cell}>
              {buttons.length > 0 ? (
                <OverflowContainer>
                  {content}
                  <FloatingActionButtons isAttached={false} buttons={buttons} />
                </OverflowContainer>
              ) : (
                content
              )}
            </CellContainer>
          );

          if (edit && editable(context.row.original, context)) {
            return (
              <Editor<TOption, IsMulti, TExtraColumnDef>
                id={id}
                callback={(...args) => edit.onChange(context.row, ...args)}
                value={value}
                trigger={preview}
                isMulti={isMulti}
                {...edit}
                components={components}
              />
            );
          }

          return preview;
        },

        meta: {
          ...baseColumn.meta,
          editable,
          bulkEdit: ifBulkEditable(
            edit,
            bulkEdit,
            () =>
              bulkEdit &&
              useBulkChange && {
                ...bulkEdit,
                id,
                editor: (editorRows, trigger, isInMenu, onOpenChange, labelledBy) => (
                  <BulkEditor<TData, TOption, IsMulti, TExtraColumnDef>
                    key={id}
                    id={id}
                    label={bulkEdit.label}
                    labelledBy={labelledBy}
                    isInMenu={isInMenu}
                    trigger={trigger}
                    rows={editorRows}
                    useBulkChange={useBulkChange}
                    onClose={bulkEdit.onClose}
                    onOpenChange={onOpenChange}
                    isMulti={columnDef.isMulti}
                    stayOpenOnChange={bulkEdit.stayOpenOnChange}
                    hideClearIndicator={bulkEdit.hideClearIndicator ?? columnDef.isMulti === true}
                    {...edit}
                    isClearable={
                      bulkEdit.isClearable ??
                      (columnDef.isMulti === true ? true : (edit?.isClearable ?? false))
                    }
                    value={getValue(editorRows, id, columnDef.isMulti)}
                    components={components}
                  />
                ),
              },
          ),
        },
      };
    };
  };
}

type EditorProps<TOption, IsMulti extends boolean, TExtraColumnDef> = Omit<
  Partial<ComboboxProps<TOption, IsMulti>>,
  'components' | 'isMulti' | 'onChange' | 'value'
> & {
  id: string;
  callback: (value: OnChangeValue<TOption, IsMulti>, meta: ActionMeta<TOption>) => unknown;
  value: OnChangeValue<TOption, IsMulti> | undefined;
  trigger: JSX.Element;
  isBulk?: boolean;
  isInMenu?: boolean;
  isMulti?: IsMulti;
  components: OptionHelperConfig<TOption, TExtraColumnDef>['components'];
  closeOnChange?: boolean;
  label?: string;
  labelledBy?: string;
  onClose?: () => void;
  onOpenChange?: (isOpen: boolean) => void;
};

const Editor = <TOption, IsMulti extends boolean, TExtraColumnDef>({
  id,
  callback,
  value,
  trigger,
  isBulk,
  isInMenu,
  isMulti,
  components,
  closeOnChange,
  label,
  labelledBy,
  onClose,
  onOpenChange,
  ...edit
}: EditorProps<TOption, IsMulti, TExtraColumnDef>) => (
  <EditablePopover
    key={id}
    onClose={onClose}
    onOpenChange={onOpenChange}
    openEvent={isInMenu ? 'mousedown' : undefined}
    placement={isBulk ? (isInMenu ? 'left-start' : 'top-end') : undefined}
    closeOnBlur={!isBulk}
  >
    <EditablePopover.Trigger>{trigger}</EditablePopover.Trigger>
    <EditablePopover.Content
      borderRadius="sm"
      outline="2px solid"
      outlineColor="chakra-border-color"
      offset={isBulk ? -1 : undefined}
      data-testid={isBulk ? `bulk-editor-${id}` : undefined}
    >
      {(context) => (
        <components.Edit
          {...edit}
          variant="popover"
          size="sm"
          placeholder=""
          autoFocus
          menuIsOpen
          isMulti={isMulti}
          value={value}
          onChange={async (newValue, meta) => {
            const result = await callback(newValue, meta);

            if (closeOnChange && result !== false) {
              context.setOpen(false);
            }
          }}
          aria-label={label}
          aria-labelledby={label ? undefined : labelledBy}
        />
      )}
    </EditablePopover.Content>
  </EditablePopover>
);

type BulkEditorProps<TData, TOption, IsMulti extends boolean, TExtraColumnDef> = Omit<
  Omit<EditorProps<TOption, IsMulti, TExtraColumnDef>, 'callback' | 'value'>,
  'defaultValue' | 'closeOnChange'
> & {
  rows: Row<TData>[];
  useBulkChange: UseBulkSelectChangeHook<TData, TOption, IsMulti>;
  value?: OnChangeValue<TOption, IsMulti>;
  labelledBy?: string;
  stayOpenOnChange?: boolean;
};

const BulkEditor = <TData, TOption, IsMulti extends boolean, TExtraColumnDef>({
  id,
  value,
  rows,
  useBulkChange,
  labelledBy,
  stayOpenOnChange,
  onOpenChange,
  onClose,
  ...props
}: BulkEditorProps<TData, TOption, IsMulti, TExtraColumnDef>) => {
  const [onChange, isLoading] = useBulkChange();
  const [localValue, setLocalValue] = useState(value);
  const [pendingCount, setPendingCount] = useState(0);
  const isOpenRef = useRef(false);
  const shouldSyncOnCloseRef = useRef(false);
  const shouldStayOpenOnChange = stayOpenOnChange === true;
  const isPending = pendingCount > 0;
  const isBusy = shouldStayOpenOnChange ? isPending || isLoading : isLoading;

  const syncOnClose = useCallback(() => {
    if (!shouldSyncOnCloseRef.current || pendingCount > 0) {
      return;
    }

    shouldSyncOnCloseRef.current = false;
    void onClose?.();
  }, [onClose, pendingCount]);

  useEffect(() => {
    if (!isPending && shouldSyncOnCloseRef.current && !isOpenRef.current) {
      syncOnClose();
    }
  }, [isPending, syncOnClose]);

  useEffect(() => {
    if (shouldStayOpenOnChange && isOpenRef.current) {
      return;
    }

    setLocalValue(value);
  }, [shouldStayOpenOnChange, value]);

  useSyncIsBulkLoading(id, shouldStayOpenOnChange ? false : isLoading);

  return (
    <Editor<TOption, IsMulti, TExtraColumnDef>
      key={id}
      id={id}
      callback={async (newValue, meta) => {
        const currentValue = shouldStayOpenOnChange ? localValue : value;
        if (equal(currentValue, newValue)) {
          return;
        }

        if (shouldStayOpenOnChange) {
          setLocalValue(newValue);
          shouldSyncOnCloseRef.current = true;
          setPendingCount((count) => count + 1);
        }

        let result;
        try {
          result = await onChange(rows, newValue, meta);
        } catch (error) {
          if (shouldStayOpenOnChange) {
            shouldSyncOnCloseRef.current = false;
            setLocalValue(currentValue);
          }
          throw error;
        } finally {
          if (shouldStayOpenOnChange) {
            setPendingCount((count) => Math.max(0, count - 1));
          }
        }

        if (!shouldStayOpenOnChange) {
          return;
        }

        if (result === false) {
          shouldSyncOnCloseRef.current = false;
          setLocalValue(currentValue);
        }
      }}
      value={shouldStayOpenOnChange ? localValue : value}
      isBulk
      onOpenChange={(isOpen) => {
        isOpenRef.current = isOpen;
        onOpenChange?.(isOpen);
      }}
      onClose={syncOnClose}
      labelledBy={labelledBy}
      isLoading={shouldStayOpenOnChange ? isBusy : undefined}
      {...props}
      closeOnChange={shouldStayOpenOnChange ? false : undefined}
    />
  );
};

// Re-export for callers that previously imported option types via this module.
export type { OptionColumnConfig, OptionColumnDef };
